import { isAfter } from 'date-fns';

const MS_IN_SECOND = 1000;
const MS_IN_MINUTE = 60 * MS_IN_SECOND;
const MS_IN_HOUR = 60 * MS_IN_MINUTE;
const MS_IN_DAY = 24 * MS_IN_HOUR;
const MS_IN_WEEK = 7 * MS_IN_DAY;
const MS_IN_MONTH = 30 * MS_IN_DAY;
const MS_IN_YEAR = 365 * MS_IN_DAY;

/**
 * Describes how the resolution should be progressively computed.
 * Contains the interval value in ms and the suffix to apply
 */
const RESOLUTION_PROGRESS: [number, string][] = [
  [MS_IN_YEAR, 'y'],
  [MS_IN_MONTH, 'mo'],
  [MS_IN_WEEK, 'wk'],
  [MS_IN_DAY, 'd'],
  [MS_IN_HOUR, 'h'],
  [MS_IN_MINUTE, 'min'],
  [MS_IN_SECOND, 's'],
  [1, 'ms'],
];

/** Performs integer division, returns the result and the remainder */
function intDivision(dividend: number, divisor: number): [number, number] {
  return [Math.floor(dividend / divisor), dividend % divisor];
}

/**
 * Compute the resolution for the given date interval.
 * @param from - start date.
 * @param to - end date.
 * @param limit - number of units to display
 * @returns a string representing the resolution or undefined if the interval is null or invalid.
 * @public
 */
export function formatResolution(
  from: Date,
  to: Date,
  limit?: number,
): string | undefined {
  if (!isAfter(to, from)) {
    return undefined;
  }

  let diffInMs = to.getTime() - from.getTime();
  const arrayLengthLimit = limit ?? RESOLUTION_PROGRESS.length;
  const resolutionsArray: string[] = [];

  RESOLUTION_PROGRESS.forEach(([intervalInMs, suffix], iterator) => {
    if (resolutionsArray.length < arrayLengthLimit) {
      const [divisionResult, remainder] = intDivision(diffInMs, intervalInMs);
      if (divisionResult > 0) {
        resolutionsArray.push(`${divisionResult}${suffix}`);
      }
      diffInMs = remainder;
    }
  });

  return resolutionsArray.length > 0 ? resolutionsArray.join(' ') : undefined;
}
